import type {LayerSpecification} from '@maplibre/maplibre-gl-style-spec';

/**
 * A data-driven paint value from an Esri vector tile stylesheet. Two forms appear:
 * `{property, stops}` interpolates the feature's value across the stops, and
 * `{property, type: 'identity'}` uses the feature's value as the paint value itself.
 */
interface DataDrivenPaintValue {
  property?: string;
  default?: unknown;
  stops?: [number, unknown][];
  type?: string;
}

const isDataDrivenPaintValue = (value: object) =>
  'stops' in value || 'default' in value || 'property' in value;

/**
 * Interpolates a numeric feature value across a paint property's stops.
 * @param value the feature's value for the property the stops are keyed on
 * @param stops the stylesheet's [input, output] pairs
 * @param defaultValue used when there are no usable stops, or the value is not a number
 */
const interpolateStops = (value: unknown, stops: unknown, defaultValue: unknown): unknown => {
  if (!Array.isArray(stops) || stops.length === 0 || typeof value !== 'number') {
    return defaultValue;
  }

  for (let i = 0; i < stops.length - 1; i++) {
    const [stopValue, stopResult] = stops[i];
    const [nextStopValue, nextStopResult] = stops[i + 1];

    if (value === stopValue) return stopResult;
    if (value > stopValue && value < nextStopValue) {
      // Only numbers can be interpolated. Colors step down to the lower stop rather than being
      // concatenated into a nonsense string like '#FF0000NaN'.
      if (typeof stopResult !== 'number' || typeof nextStopResult !== 'number') return stopResult;
      const t = (value - stopValue) / (nextStopValue - stopValue);
      return stopResult + t * (nextStopResult - stopResult);
    }
  }

  return value < stops[0][0] ? stops[0][1] : stops[stops.length - 1][1];
};

/**
 * Resolves a `paint` or `layout` value from a vector tile stylesheet to a concrete value. Literals are
 * returned unchanged and MapLibre expression arrays return undefined, since we cannot evaluate them.
 * @param paintValue the raw value read off layer.paint or layer.layout
 * @param featureProperties the feature's properties. Omit when there is no single feature, as when building
 * a legend entry, to resolve to the value's default.
 */
const resolvePaintValue = (paintValue: unknown, featureProperties?: Record<string, unknown> | null): unknown => {
  if (paintValue === null || paintValue === undefined) return undefined;
  if (Array.isArray(paintValue)) return undefined;
  if (typeof paintValue !== 'object') return paintValue;
  if (!isDataDrivenPaintValue(paintValue)) return undefined;

  const {property, default: defaultValue, stops, type} = paintValue as DataDrivenPaintValue;
  if (!featureProperties || !property) return defaultValue;
  if (type === 'identity') return featureProperties[property] ?? defaultValue;
  return interpolateStops(featureProperties[property], stops, defaultValue);
};

/** Reads one paint property off a style layer as a color string, or null when it is absent or unreadable. */
const getPaintColor = (layer: LayerSpecification | undefined, property: string): string | null => {
  // Paint is cast because the LayerSpecification union does not allow indexing by paint properties across
  // different layer types.
  const paint = layer?.paint as Record<string, unknown> | undefined;
  const color = resolvePaintValue(paint?.[property]);
  return typeof color === 'string' && color.trim() !== '' ? color : null;
};

export {
  resolvePaintValue,
  getPaintColor,
};
